import { apiUrl } from "../apiBase";
// src/components/SentimentSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import MetricCard from "./MetricCard";
import ChartModal from "./ChartModal";
import "./Overview.css";

// --- Metric configuration (add/edit descriptions any time) ---
const SENTIMENT_METRICS = {
  vix: {
    id: "vix",
    title: "VIX",
    subtitle: "Equity Volatility",
    icon: "📉",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QL6M",
    description:
      "VIX is the market’s implied volatility for the S&P 500 over the next ~30 days. Higher VIX usually means more fear and bigger expected swings. Low VIX often lines up with calm markets, but it can also be complacency.",
  },
  hy_oas: {
    id: "hy_oas",
    title: "HY OAS",
    subtitle: "High-Yield Spread (bp)",
    icon: "💳",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QL8j",
    description:
      "High-yield spreads measure how much extra interest investors demand to hold riskier corporate debt versus Treasuries. Rising HY spreads usually mean risk appetite is weakening and default risk concerns are rising.",
  },
  ig_oas: {
    id: "ig_oas",
    title: "IG OAS",
    subtitle: "Investment-Grade Spread (bp)",
    icon: "🏦",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QL8a",
    description:
      "Investment-grade spreads track credit conditions for stronger companies. They move less than HY spreads. If IG spreads widen noticeably, it can signal broader stress in funding markets.",
  },
  financial_stress: {
    id: "financial_stress",
    title: "Financial Stress",
    subtitle: "St. Louis Fed Index",
    icon: "⚠️",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8d0",
    description:
      "A composite index built from multiple market signals. Higher readings generally mean more stress in financial conditions. It tends to spike during shocks and crisis periods.",
  },
  yield10y: {
    id: "yield10y",
    title: "10Y Yield",
    subtitle: "Long-term Treasury",
    icon: "📊",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O5dK",
    description:
      "The 10-year Treasury yield is a key benchmark for long-term borrowing costs (mortgages, corporate rates). Rising yields can tighten conditions and pressure valuations; falling yields can mean growth fears or a flight to safety.",
  },
  t_bill_3m: {
    id: "t_bill_3m",
    title: "3M T-Bill",
    subtitle: "Short-term rate",
    icon: "💵",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8QE",
    description:
      "A classic ‘cash rate’ proxy. It stays close to Fed policy expectations. If 3M yields stay high while long yields fall, it often means the curve is flattening/inverting and policy is tight.",
  },
  yield_curve: {
    id: "yield_curve",
    title: "Yield Curve",
    subtitle: "10Y minus 3M",
    icon: "📈",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8e3",
    description:
      "The curve spread (10Y minus 3M) is a popular recession-risk signal. When it goes negative (inverted), it often reflects tight policy and weaker growth expectations.",
  },
  dxy: {
    id: "dxy",
    title: "Dollar Index",
    subtitle: "Broad Trade-Weighted USD",
    icon: "💲",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8RE",
    description:
      "A stronger dollar can tighten global financial conditions and pressure commodities and emerging markets. A weaker dollar can support risk assets and commodities, but may also add inflation pressure through imports.",
  },
};

const SENTIMENT_HISTORY_KEY = {
  vix: "vix",
  hy_oas: "hyOas",
  ig_oas: "igOas",
  financial_stress: "stress",
  yield10y: "y10",
  t_bill_3m: "t3m",
  yield_curve: "curve",
  dxy: "dxy",
};

const CARD_ORDER = [
  "vix",
  "hy_oas",
  "ig_oas",
  "financial_stress",
  "yield10y",
  "t_bill_3m",
  "yield_curve",
  "dxy",
];

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return res.json();
}

// --- Mini chart (simple SVG) ---
// ✅ FIX: FRED often returns missing values as "." which becomes NaN and breaks the SVG path.
// We clean the series first and only plot valid numeric points.
const VixMiniLineChart = ({ series }) => {
  const w = 560;
  const h = 240; // a bit taller for axes
  const padL = 44; // left padding for y-axis labels
  const padR = 14;
  const padT = 14;
  const padB = 34; // bottom padding for x-axis labels

  const [hoverIdx, setHoverIdx] = useState(null);

  const fmtDateShort = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const fmtDateLong = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const model = useMemo(() => {
    if (!Array.isArray(series) || series.length < 2) return null;

    const clean = series
      .map((d) => ({
        date: d.date ?? d.observation_date ?? null,
        v: Number(d.value),
      }))
      .filter((d) => Number.isFinite(d.v));

    if (clean.length < 2) return null;

    const vals = clean.map((d) => d.v);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;

    const innerW = w - padL - padR;
    const innerH = h - padT - padB;

    const step = innerW / (clean.length - 1);

    const points = clean.map((d, i) => {
      const x = padL + i * step;
      const y = padT + innerH * (1 - (d.v - min) / range);
      return { x, y, v: d.v, date: d.date };
    });

    // nice tick values
    const yTicks = 4; // number of intervals (gives 5 lines incl top/bottom)
    const yLines = Array.from({ length: yTicks + 1 }, (_, k) => {
      const t = k / yTicks; // 0..1
      const y = padT + innerH * t;
      const value = max - (max - min) * t;
      return { y, value };
    });

    // x labels: start, mid, end
    const idxMid = Math.floor((clean.length - 1) / 2);
    const xLabels = [
      { idx: 0, text: fmtDateShort(clean[0].date), x: points[0].x },
      { idx: idxMid, text: fmtDateShort(clean[idxMid].date), x: points[idxMid].x },
      {
        idx: clean.length - 1,
        text: fmtDateShort(clean[clean.length - 1].date),
        x: points[points.length - 1].x,
      },
    ];

    const pathD = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(" ");

    return { points, pathD, min, max, innerW, innerH, yLines, xLabels };
  }, [series]);

  if (!model) {
    return <div style={{ padding: "14px 0", color: "var(--text-secondary)" }}>No VIX history yet.</div>;
  }

  const { points, pathD, yLines, xLabels } = model;

  const handleMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const clientX =
      e.clientX != null ? e.clientX : e.touches && e.touches[0] ? e.touches[0].clientX : null;
    if (clientX == null) return;

    const localX = clientX - rect.left;
    const viewX = (localX / rect.width) * w;

    // nearest by x (points are ordered)
    // estimate using step
    const innerW = w - padL - padR;
    const step = innerW / (points.length - 1);
    const idx = Math.round((viewX - padL) / step);
    const clamped = Math.max(0, Math.min(points.length - 1, idx));
    setHoverIdx(clamped);
  };

  const handleLeave = () => setHoverIdx(null);

  const active = hoverIdx == null ? points[points.length - 1] : points[hoverIdx];

  // Tooltip positioning
  const tooltipW = 160;
  const tooltipH = 48;
  const tipX = Math.min(Math.max(active.x - tooltipW / 2, 6), w - tooltipW - 6);
  const tipY = Math.max(active.y - tooltipH - 10, 6);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="VIX history"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onTouchStart={handleMove}
      onTouchMove={handleMove}
      onTouchEnd={handleLeave}
      style={{ touchAction: "none", cursor: "crosshair" }}
    >
      {/* background */}
      <rect x="0" y="0" width={w} height={h} rx="12" ry="12" fill="transparent" />

      {/* horizontal grid + y-axis labels */}
      {yLines.map((t, i) => (
        <g key={i}>
          <line
            x1={padL}
            y1={t.y}
            x2={w - padR}
            y2={t.y}
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.07"
          />
          <text
            x={padL - 10}
            y={t.y + 4}
            textAnchor="end"
            fontSize="11"
            fill="var(--text-secondary)"
          >
            {t.value.toFixed(0)}
          </text>
        </g>
      ))}

      {/* axis lines */}
      <line
        x1={padL}
        y1={padT}
        x2={padL}
        y2={h - padB}
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.12"
      />
      <line
        x1={padL}
        y1={h - padB}
        x2={w - padR}
        y2={h - padB}
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.12"
      />

      {/* line */}
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth="2" opacity="0.9" />

      {/* x-axis labels */}
      {xLabels.map((l, i) => (
        <text
          key={i}
          x={l.x}
          y={h - 12}
          textAnchor={i === 0 ? "start" : i === xLabels.length - 1 ? "end" : "middle"}
          fontSize="11"
          fill="var(--text-secondary)"
        >
          {l.text}
        </text>
      ))}

      {/* crosshair */}
      <line
        x1={active.x}
        y1={padT}
        x2={active.x}
        y2={h - padB}
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.20"
      />
      <line
        x1={padL}
        y1={active.y}
        x2={w - padR}
        y2={active.y}
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.10"
      />

      {/* active dot */}
      <circle cx={active.x} cy={active.y} r="5" fill="currentColor" />

      {/* tooltip */}
      <g>
        <rect x={tipX} y={tipY} width={tooltipW} height={tooltipH} rx="10" ry="10" fill="rgba(0,0,0,0.72)" />
        <text x={tipX + 10} y={tipY + 18} fill="white" fontSize="12" fontWeight="700">
          VIX {active.v.toFixed(2)}
        </text>
        <text x={tipX + 10} y={tipY + 36} fill="rgba(255,255,255,0.85)" fontSize="11">
          {fmtDateLong(active.date)}
        </text>
      </g>
    </svg>
  );
};

const InfoModal = ({ isOpen, metric, onClose }) => {
  if (!metric) return null;

  return (
    <div
      className={`modal-backdrop ${isOpen ? "modal-open" : "modal-closed"}`}
      onClick={onClose}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>How to read: {metric.title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close info">
            ×
          </button>
        </div>
        <div className="modal-description">
          {metric.description || "No description configured for this metric yet."}
        </div>
      </div>
    </div>
  );
};

// ✅ Adapter: backend snapshot -> MetricCard-friendly map
function adaptBackendSnapshotToMap(snapshot) {
  const cards = snapshot?.cards || {};

  const mk = (value, date) => ({
    value: value ?? null,
    prevValue: null,
    status: value == null ? "error" : "ok",
    timestamp: date ?? null,
  });

  return {
    vix: mk(cards?.vix?.value, cards?.vix?.date),
    hy_oas: mk(cards?.hyOas?.value, cards?.hyOas?.date),
    ig_oas: mk(cards?.igOas?.value, cards?.igOas?.date),
    financial_stress: mk(cards?.stressIndex?.value, cards?.stressIndex?.date),
    yield10y: mk(cards?.tenYear?.value, cards?.tenYear?.date),
    t_bill_3m: mk(cards?.threeMonth?.value, cards?.threeMonth?.date),
    yield_curve: mk(cards?.curveSlope?.value, cards?.curveSlope?.date),
    dxy: mk(cards?.dollarIndex?.value, cards?.dollarIndex?.date),
  };
}

const SentimentSection = () => {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sentimentHistory, setSentimentHistory] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);
  const [isChartOpen, setIsChartOpen] = useState(false);
  const [activeInfoMetric, setActiveInfoMetric] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const fetchSentimentHistory = async () => {
  try {
    const res = await fetch(apiUrl("/api/sentiment-history"));
    if (!res.ok) throw new Error("Failed to fetch sentiment history");
    const json = await res.json();
    setSentimentHistory(json);
  } catch (e) {
    console.error("Sentiment history error:", e);
    setSentimentHistory(null);
  }
};

useEffect(() => {
  fetchSentimentHistory();
  const id = setInterval(fetchSentimentHistory, 20 * 60 * 1000); // 20 min
  return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        // ✅ FIX: only call the real endpoint you actually have
        const json = await fetchJson(apiUrl("/api/sentiment"));

        if (cancelled) return;

        // (If you ever change backend to send json.metrics array, this still supports it)
        if (Array.isArray(json.metrics)) {
          const map = {};
          json.metrics.forEach((m) => {
            map[m.id] = {
              value: m.latestValue ?? null,
              prevValue: m.prevValue ?? null,
              status: m.error ? "error" : "ok",
              timestamp: m.latestDate || null,
            };
          });

          setData({
            map,
            vixHistory: json.vixHistory || json.vix_history || [],
            compositeScore: json.compositeScore ?? json.composite_score ?? null,
            compositeText: json.compositeText ?? json.composite_text ?? null,
          });

          setAsOf(json.asOf || json.as_of || null);
          return;
        }

        // Backend snapshot shape:
        // { asOf, cards: {...}, vixHistory: [...], sentiment: {score,label,description} }
        const adaptedMap = adaptBackendSnapshotToMap(json);

        setData({
          map: adaptedMap,
          vixHistory: json.vixHistory || json.vix_history || [],
          compositeScore: json?.sentiment?.score ?? null,
          compositeText: json?.sentiment?.description ?? null,
        });

        setAsOf(json?.asOf || json?.as_of || null);
      } catch (e) {
        console.error("Sentiment fetch error:", e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const openChart = (metricCfg) => {
    setActiveMetric(metricCfg);
    setIsChartOpen(true);
  };

  const closeChart = () => {
    setIsChartOpen(false);
    setActiveMetric(null);
  };

  const openInfo = (metricCfg) => {
    setActiveInfoMetric(metricCfg);
    setIsInfoOpen(true);
  };

  const closeInfo = () => {
    setIsInfoOpen(false);
    setActiveInfoMetric(null);
  };

  const compositeScore = data?.compositeScore;

  // ✅ FIX: make bar actually react; if score is missing, don't fake 50
  const scoreNum = Number(compositeScore);
  const hasScore = Number.isFinite(scoreNum);
  const barPct = hasScore ? Math.min(Math.max(scoreNum, 0), 100) : 0;

  const compositeLabel =
    compositeScore == null ? "Neutral" : scoreNum < 40 ? "Fear" : scoreNum > 60 ? "Greed" : "Neutral";

  const compositeText =
    data?.compositeText ||
    `Composite Sentiment & Risk score is ${hasScore ? `${scoreNum.toFixed(0)}/100` : "—"}. It blends volatility (VIX), credit spreads, financial stress, the yield curve, and the dollar into a single view of risk appetite.`;

  if (loading && !data) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner"></div>
        <p>Loading sentiment data...</p>
      </div>
    );
  }

  return (
    <div className="overview">
      <div className="summary-card market-tone">
        <h3>Overall Market Tone: {compositeLabel}</h3>
        <p>{compositeText}</p>
      </div>

      <section className="overview-block">
        <div className="block-header" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h2>Sentiment &amp; Risk</h2>
            <p>FRED-based view of volatility, credit, financial stress, rates and FX.</p>
          </div>

          {asOf && (
            <div style={{ alignSelf: "flex-start" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "6px 10px",
                  borderRadius: 999,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  border: "1px solid var(--border-light)",
                  color: "var(--text-secondary)",
                  background: "var(--bg-primary)",
                  whiteSpace: "nowrap",
                }}
              >
                As of {asOf}
              </span>
            </div>
          )}
        </div>

        <div className="metrics-grid">
          {CARD_ORDER.map((id) => {
            const cfg = SENTIMENT_METRICS[id];
            const hk = SENTIMENT_HISTORY_KEY[id];
            const sparklineData = hk ? sentimentHistory?.[hk] : null;

            return (
              <MetricCard
                key={id}
                metric={cfg}
                data={data?.map?.[id] || null}
                onClick={() => openChart(cfg)}
                onInfoClick={() => openInfo(cfg)}
                sparklineData={sparklineData}
                sparklineLabel={sparklineData ? "Last 3 months" : undefined}
              />
            );
          })}
        </div>

        <div className="yield-curve-section" style={{ marginTop: 24 }}>
          <div className="chart-card">
            <div className="chart-title">VIX Historical</div>
            <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: 8 }}>
              Last 3 months of daily closing volatility (FRED VIXCLS).
            </div>
            <div
              style={{
                borderRadius: 12,
                background: "var(--bg-section)",
                border: "1px solid var(--border-light)",
                padding: 14,
                color: "var(--accent-primary)",
              }}
            >
              <VixMiniLineChart series={data?.vixHistory} />
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-title">Sentiment &amp; Risk</div>
            <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: 10 }}>
              Composite view of volatility, credit, stress, curve and FX.
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>{compositeLabel}</div>
              <div style={{ fontWeight: 800, color: "var(--text-primary)" }}>
                {hasScore ? `${scoreNum.toFixed(0)}/100` : "—"}
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                borderRadius: 999,
                overflow: "hidden",
                border: "1px solid var(--border-light)",
                background: "var(--bg-section)",
                height: 10,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${barPct}%`,
                  background: "linear-gradient(90deg, #f97316, #facc15, #22c55e)",
                  opacity: hasScore ? 1 : 0.25,
                }}
              />

              {/* middle marker */}
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: -4,
                  width: 2,
                  height: 18,
                  background: "rgba(0,0,0,0.35)",
                }}
              />

              {/* ✅ marker dot like your screenshot (more obvious) */}
              {hasScore && (
                <div
                  style={{
                    position: "absolute",
                    left: `calc(${barPct}% - 6px)`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: "var(--bg-primary)",
                    border: "2px solid var(--text-primary)",
                  }}
                />
              )}
            </div>

            <div style={{ marginTop: 10, color: "var(--text-secondary)", fontSize: "0.82rem" }}>
              {compositeText}
            </div>
          </div>
        </div>

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              Start with the Sentiment &amp; Risk gauge: lower scores (0–40) lean toward Fear, higher scores
              (60–100) toward Greed. Then scan the cards (VIX, credit spreads, financial stress, yield curve,
              dollar) to see which area is driving the move.
            </p>
          </div>
        </div>
      </section>

      <ChartModal isOpen={isChartOpen} metric={activeMetric} onClose={closeChart} />
      <InfoModal isOpen={isInfoOpen} metric={activeInfoMetric} onClose={closeInfo} />
    </div>
  );
};

export default SentimentSection;